import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import type { Currency } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import FormattedTimeZone from '@swf/components/FormattedTimeZone/FormattedTimeZone';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import type { InstantBonusInfo, PreCashinInfo } from '../../../../../types/Cashier';
import { isZeroExponentCurrency } from '../../../../../utils/cashier';

import {
  TXTCLI_Casino,
  TXTCLI_If_your_deposit_is_approvedX_N_ELL,
  TXTCLI_Poker,
  TXTCLI_Sports,
  TXTCLI_The_bonus_will_be_credited_in__ELL,
  TXTCLI_The_code_you_have_entered_with_ELL,
  TXTCLI_The_code_you_have_entered_with_ELL_3,
  TXTCLI_You_can_use_your_Instant_Bonus_ELL,
  TXTCLI_You_will_need_to_earn_N0_FPPs__ELL,
  TXTCLI_You_will_need_to_place_N0_bets_ELL,
  TXTWCL_An_Instant_Bonus_of_N0X_will_b_ELL,
  TXTWCL_XThe_exchange_rate_from_N0_to__ELL
} from './InstantBonus.messages.json';
import { info } from './InstantBonus.scss';
import TermsAndConditionsLink from './components/TermsAndConditionsLink';

const getProductName = (product: string) => {
  const map: Record<string, TranslationTag> = {
    TXTCLI_Casino,
    TXTCLI_Poker,
    TXTCLI_Sports
  };
  return <Translation {...map[product]} />;
};

const getExpiryTime = (bonusEnd: number) => (
  <FormattedTimeZone
    date={bonusEnd}
    month="short"
    day="2-digit"
    year="numeric"
    hour="numeric"
    minute="numeric"
    timeZoneName="short"
    hour12={false}
  />
);

const getBonusAmount = (value: number, currency: Currency) => (
  <CurrencyAmount
    value={value}
    currency={currency}
    isZeroExponentCurrency={isZeroExponentCurrency(currency)}
    currencyDisplay="code"
  />
);

const InstantBonus: React.FC = () => {
  const license = useSelector(select.account.getLicense);
  const {
    data: {
      preCashinInfo,
      paySystemDetails: {
        rates: { curr: balanceCurrency }
      }
    },
    values: { deposit }
  } = useWizardContext();
  const { currency, amount } = deposit;
  const { instantBonusInfo, balanceAmount, bonusName, bonusEnd } = preCashinInfo as PreCashinInfo;
  const {
    bonusCurrency,
    bonusAmountInBonusCurrency,
    products,
    cashPrice,
    cashPriceCurrency,
    betsToClear,
    pointsToClear
  } = instantBonusInfo as InstantBonusInfo;
  const isBonusCurrencyZeroExponent = isZeroExponentCurrency(bonusCurrency);
  const bonusRedeemAmount = betsToClear || pointsToClear / 100.0;
  const productName = getProductName(products[0]);
  const isBonusWithCost = cashPrice !== 0;
  const bonusAmount = getBonusAmount(bonusAmountInBonusCurrency, bonusCurrency);

  return (
    <>
      <Translation
        tagName="p"
        {...(cashPrice !== 0
          ? TXTCLI_The_code_you_have_entered_with_ELL
          : TXTCLI_The_code_you_have_entered_with_ELL_3)}
        values={{
          0: (
            <CurrencyAmount
              value={balanceAmount}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBonusCurrencyZeroExponent}
              currencyDisplay="code"
            />
          ),
          1: (
            <CurrencyAmount
              value={amount}
              currency={currency}
              isZeroExponentCurrency={isZeroExponentCurrency(currency)}
              dollarValue
              currencyDisplay="code"
            />
          ),
          2: productName,
          3: bonusName,
          4: bonusAmount
        }}
      />
      {isBonusWithCost && (
        <>
          <Translation
            tagName="p"
            {...TXTCLI_If_your_deposit_is_approvedX_N_ELL}
            values={{
              0: (
                <CurrencyAmount
                  value={cashPrice}
                  currency={cashPriceCurrency}
                  isZeroExponentCurrency={isZeroExponentCurrency(cashPriceCurrency)}
                  currencyDisplay="code"
                />
              )
            }}
          />
          <p>
            <Translation
              tagName="b"
              {...TXTWCL_An_Instant_Bonus_of_N0X_will_b_ELL}
              values={{ 0: bonusAmount }}
            />
          </p>
        </>
      )}
      <Translation
        tagName="p"
        {...TXTCLI_The_bonus_will_be_credited_in__ELL}
        values={{ 0: bonusCurrency }}
      />
      <Translation
        tagName="p"
        {...TXTCLI_You_can_use_your_Instant_Bonus_ELL}
        values={{ 0: productName }}
      />
      <Translation
        tagName="p"
        {...(betsToClear
          ? TXTCLI_You_will_need_to_place_N0_bets_ELL
          : TXTCLI_You_will_need_to_earn_N0_FPPs__ELL)}
        values={{
          0: bonusRedeemAmount,
          1: getExpiryTime(bonusEnd)
        }}
      />
      {!betsToClear && isBonusWithCost && balanceCurrency !== bonusCurrency && (
        <p className={info}>
          <Translation
            {...TXTWCL_XThe_exchange_rate_from_N0_to__ELL}
            values={{
              0: balanceCurrency,
              1: bonusCurrency
            }}
          />
        </p>
      )}
      {license === LicenceId.UK && <TermsAndConditionsLink isBonusWithCost={isBonusWithCost} />}
    </>
  );
};

export default InstantBonus;
